"use client";

import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from "react";
import { encodePublicCompositionLocation, parsePublicCompositionLocation, type PublicCompositionLocation } from "@/lib/virtualspace/public-composition-location";

const LOCATION_EVENT = "vs:composition-location";
const CompositionContext = createContext({ enabled: false, privatePreview: false, publicShareUrl: null as string | null });
const BlockContext = createContext("");
const SectionContext = createContext("");
export function StandaloneCompositionProvider({ enabled = false, publicShareUrl = null, privatePreview = false, children }: { enabled?: boolean; publicShareUrl?: string | null; privatePreview?: boolean; children: ReactNode }) {
  return <CompositionContext.Provider value={{ enabled, publicShareUrl, privatePreview }}><CompositionSectionRestore enabled={enabled} />{children}</CompositionContext.Provider>;
}
export const PublicCompositionSectionProvider = SectionContext.Provider;
export const usePublicCompositionSectionId = () => useContext(SectionContext);
export const PublicCompositionBlockProvider = BlockContext.Provider;
export const usePublicComposition = () => useContext(CompositionContext);
export const usePublicCompositionBlockId = () => useContext(BlockContext);
function subscribe(listener: () => void) {
  window.addEventListener("popstate", listener);
  window.addEventListener("hashchange", listener);
  window.addEventListener(LOCATION_EVENT, listener);
  return () => {
    window.removeEventListener("popstate", listener);
    window.removeEventListener("hashchange", listener);
    window.removeEventListener(LOCATION_EVENT, listener);
  };
}
const noopSubscribe = () => () => {};
export function usePublicCompositionLocation(enabled: boolean) {
  const hash = useSyncExternalStore(enabled ? subscribe : noopSubscribe, () => enabled ? window.location.hash : "", () => "");
  return parsePublicCompositionLocation(hash);
}
export function setPublicCompositionLocation(patch: PublicCompositionLocation, replace = false) {
  const current = parsePublicCompositionLocation(window.location.hash);
  const next = encodePublicCompositionLocation({ ...current, ...patch });
  if (next === window.location.hash) return;
  const url = `${window.location.pathname}${window.location.search}${next}`;
  window.history[replace ? "replaceState" : "pushState"](window.history.state, "", url);
  window.dispatchEvent(new Event(LOCATION_EVENT));
}

/** Account for the actual responsive sticky header without changing shared/embed CSS. */
export function scrollPublicCompositionSection(target: HTMLElement, behavior: ScrollBehavior = "auto") {
  const header = document.querySelector<HTMLElement>("[data-vs-composition-header]");
  if (!header) {
    target.scrollIntoView({ behavior, block: "start" });
    return;
  }
  const height = header.getBoundingClientRect().height;
  const stickyTop = Math.max(0, Number.parseFloat(window.getComputedStyle(header).top) || 0);
  const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - height - stickyTop - 12);
  window.scrollTo({ top, behavior });
}

function CompositionSectionRestore({ enabled }: { enabled: boolean }) {
  const { section } = usePublicCompositionLocation(enabled);
  useEffect(() => {
    if (!enabled || !section) return;
    const restore = () => {
      const target = document.getElementById(`vs-section-${section}`);
      // Only actual rendered Space rows can be targeted by a presentation URL.
      if (!target || target.dataset.vsRailBlockId !== section) return false;
      scrollPublicCompositionSection(target);
      return true;
    };
    if (restore()) return;
    const observer = new MutationObserver(() => { if (restore()) observer.disconnect(); });
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = setTimeout(() => observer.disconnect(), 10000);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [enabled, section]);
  return null;
}

/** Delay expensive viewers until near the viewport; retain mounted state after scrolling away. */
export function CompositionViewerMount({ children, label = "viewer", height }: { children: ReactNode; label?: string; height?: CSSProperties["height"] }) {
  const { enabled } = usePublicComposition();
  const [started, setStarted] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!enabled || started) return;
    if (typeof IntersectionObserver === "undefined") { setStarted(true); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setStarted(true); observer.disconnect(); }
    }, { rootMargin: "300px" });
    if (host.current) observer.observe(host.current);
    return () => observer.disconnect();
  }, [enabled, started]);
  if (!enabled) return children;
  return <div ref={host} className="h-full w-full" style={height == null ? undefined : { height }} data-vs-deferred-viewer={started ? "ready" : "waiting"}>
    {started ? children : <div className="flex h-full items-center justify-center bg-muted/30 p-4">
      <button type="button" className="vs-btn-outline min-h-11 px-4" onClick={() => setStarted(true)}>Load {label}</button>
    </div>}
  </div>;
}
